import { Button, Card, Checkbox, Col, Divider, Flex, Form, FormProps, Row } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import useCooperative from 'core/hooks/useCooperative';
import { useTranslation } from 'react-i18next';

//services
import NotificationService from 'core/services/notification';

// components
import FileInput from 'components/fileInput/fileInput';
import FormElement from 'components/formElement/formElement';
import ComponentHeader from 'components/componentHeader/componentHeader';
import { onUpdateDocuments, onUploadDocuments } from '../services/documentsService';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import { setCompany } from 'stores/useCompanyStore';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import { mapResponseErrors } from 'utils/mapResponseErrors';

type FormFieldType = {
	'chairman_name[ar]': string;
	'chairman_name[en]': string;
	share_average_current_value: string;
	chairman_signature: File;
	certified_contract: File;
	company_establishment_contract: File;
	tax_registration_certificate: File;
	board_of_directors: File;
	commercial_register: File;
	company_representative_certified_authorization: File;
	board_committees: File;
	certified_valuation_and_share_value: File;
};

const UploadDocumentComponent: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [documentsForm] = Form.useForm();

	const company = useSelector((state: RootState) => state.company);
	const { isCooperative } = useCooperative();

	const [acknowledge, setAcknowledge] = useState(false);
	const [specificFiles, setSpecificFiles] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (company?.profile_status === 'FieldUpdateRequest' && company.field_update_request) {
			setSpecificFiles(company.field_update_request);
		}
	}, []);

	const validateArabic = (_: any, value: string) => {
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const onFormFinish: FormProps<FormFieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			const uploadDocumentsPayload = new FormData();
			uploadDocumentsPayload.append('_method', 'put');
			uploadDocumentsPayload.append('terms_and_conditions', 'true');

			Object.entries(values).forEach(([key, value]) => {
				if (value !== null && value !== undefined) uploadDocumentsPayload.append(key, value);
			});

			let response;
			if (company?.profile_status === 'FieldUpdateRequest') {
				response = await onUpdateDocuments(uploadDocumentsPayload);
			} else {
				response = await onUploadDocuments(uploadDocumentsPayload);
			}
			dispatch(setCompany(response.company));
			setIsLoading(false);
		} catch (error: any) {
			setIsLoading(false);
			mapResponseErrors(error, documentsForm);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<Card className="pb-3">
				<ComponentHeader title={t('DOCUMENTS.PLEASE_UPLOAD')} subtitle={t('DOCUMENTS.PLEASE_UPLOAD_DESC')} style={{ marginBottom: 40 }} />

				<Form form={documentsForm} onFinish={onFormFinish} scrollToFirstError>
					<Row gutter={[16, 16]}>
						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('certified_contract'))) && (
							<Col span={24} lg={12}>
								<Form.Item name="certified_contract" style={{ maxWidth: '100%' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('certified_contract', file)}
										accept="pdf"
										name={t('DOCUMENTS.SUBSCRIPTION_CONTRACT')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('company_establishment_contract'))) && (
							<Col span={24} lg={12}>
								<Form.Item
									name="company_establishment_contract"
									style={{ maxWidth: '100%' }}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('company_establishment_contract', file)}
										accept="pdf"
										name={t('DOCUMENTS.COMPANY_ESTABLISHMENT')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('tax_registration_certificate'))) && (
							<Col span={24} lg={12}>
								<Form.Item
									name="tax_registration_certificate"
									style={{ maxWidth: '100%' }}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('tax_registration_certificate', file)}
										accept="pdf"
										name={t('DOCUMENTS.VAT_REGISTRATION')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('board_of_directors'))) && (
							<Col span={24} lg={12}>
								<Form.Item name="board_of_directors" style={{ maxWidth: '100%' }}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('board_of_directors', file)}
										accept="pdf"
										name={t('DOCUMENTS.BOARD_OF_DIRECTORS')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('commercial_register'))) && (
							<Col span={24} lg={12}>
								<Form.Item name="commercial_register" style={{ maxWidth: '100%' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('commercial_register', file)}
										accept="pdf"
										name={t('DOCUMENTS.COMMERCIAL_REGISTRATION')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 ||
							(specificFiles.length > 0 && specificFiles.includes('company_representative_certified_authorization'))) && (
							<Col span={24} lg={12}>
								<Form.Item
									name="company_representative_certified_authorization"
									style={{ maxWidth: '100%' }}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('company_representative_certified_authorization', file)}
										accept="pdf"
										name={t('DOCUMENTS.APPROVED_AUTHORIZATION')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('board_committees'))) && (
							<Col span={24} lg={12}>
								<Form.Item name="board_committees" style={{ maxWidth: '100%' }}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('board_committees', file)}
										accept="pdf"
										name={t('DOCUMENTS.BOARD_COMMITMENT')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('chairman_signature'))) && (
							<Col span={24} lg={12}>
								<Form.Item name="chairman_signature" style={{ maxWidth: '100%' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
									<FileInput
										onSelect={(file) => documentsForm.setFieldValue('chairman_signature', file)}
										accept="photo"
										name={t('DOCUMENTS.CHAIRMAN_SIGNATURE')}
									/>
								</Form.Item>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('chairman_name'))) && (
							<Col span={24} lg={12}>
								<FormElement
									name="chairman_name[ar]"
									type="text"
									label={t('DOCUMENTS.CHAIRMAN_NAME_AR')}
									fullWidth
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
								/>
							</Col>
						)}

						{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('chairman_name'))) && (
							<Col span={24} lg={12}>
								<FormElement
									name="chairman_name[en]"
									type="text"
									label={t('DOCUMENTS.CHAIRMAN_NAME_EN')}
									fullWidth
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
									onChange={(value) =>
										documentsForm.setFieldsValue({
											'chairman_name[en]': value
												.toLowerCase()
												.split(' ')
												.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
												.join(' '),
										})
									}
								/>
							</Col>
						)}

						{!isCooperative && (
							<>
								{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('share_average_current_value'))) && (
									<Col span={24} lg={12}>
										<FormElement name="share_average_current_value" type="number" label={t('DOCUMENTS.AVERAGE_CURRENT_VALUE')} fullWidth />
									</Col>
								)}

								{(specificFiles.length === 0 || (specificFiles.length > 0 && specificFiles.includes('certified_valuation_and_share_value'))) && (
									<Col span={24} lg={12}>
										<Form.Item name="certified_valuation_and_share_value" style={{ maxWidth: '100%' }}>
											<FileInput
												onSelect={(file) => documentsForm.setFieldValue('certified_valuation_and_share_value', file)}
												accept="pdf"
												name={t('DOCUMENTS.CERTIFIED_COMPANY_VALUATION')}
											/>
										</Form.Item>
									</Col>
								)}
							</>
						)}

						<Divider />

						<Col span={24}>
							<Flex align="center" justify="space-between" gap={20}>
								<Checkbox style={{ fontSize: 16 }} onChange={(event) => setAcknowledge(event.target.checked)}>
									{t('COMMON.ACKNOWLEDGE')}
								</Checkbox>

								<Button type="primary" htmlType="submit" loading={isLoading} disabled={!acknowledge || isLoading}>
									{t('COMMON.SUBMIT')}
								</Button>
							</Flex>
						</Col>
					</Row>
				</Form>
			</Card>
		</>
	);
};

export default UploadDocumentComponent;
